import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Twitter, Instagram, Facebook, Wallet, LogOut } from 'lucide-react';
import { useWeb3, formatAddress } from '../../context/Web3Context';

const BET4PEER_LOGO = "https://customer-assets.emergentagent.com/job_83ddb6ac-1a43-447f-9a3b-123d64b2d59d/artifacts/pter9nuv_Logo%20futuriste%20Bet4Peer%20avec%20poigne%CC%81e%20de%20main.png";

const SOCIAL_LINKS = [
    {
        name: 'X (Twitter)',
        href: 'https://x.com/bet4peer',
        icon: Twitter,
        testid: 'social-x',
    },
    {
        name: 'Instagram',
        href: 'https://instagram.com/bet4peer.official',
        icon: Instagram,
        testid: 'social-instagram',
    },
    {
        name: 'Facebook',
        href: 'https://www.facebook.com/profile.php?id=61589108596119',
        icon: Facebook,
        testid: 'social-facebook',
    },
];

export function Footer() {
    const { t } = useTranslation();
    const { address, isConnected, isConnecting, connect, disconnect } = useWeb3();
    return (
        <footer className="border-t border-white/5 bg-[#0A0A0C]/80 backdrop-blur-sm" data-testid="footer" role="contentinfo" aria-label="Site footer">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
                    {/* Brand */}
                    <div className="col-span-1 md:col-span-2">
                        <Link to="/" className="flex items-center gap-3 mb-4">
                            <img 
                                src={BET4PEER_LOGO} 
                                alt="Bet4Peer" 
                                className="h-12 w-12 rounded-lg object-cover"
                            />
                            <span className="font-bold text-xl">
                                <span className="text-white">Bet</span>
                                <span className="text-[#00FF66]">4</span>
                                <span className="text-white">Peer</span>
                            </span>
                        </Link>
                        <p className="text-white/50 text-sm max-w-md leading-relaxed">
                            {t('footer.desc')}
                        </p>
                        <div className="flex items-center gap-3 mt-6" data-testid="social-links">
                            {SOCIAL_LINKS.map((s) => {
                                const Icon = s.icon;
                                return (
                                    <a
                                        key={s.testid}
                                        href={s.href}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        aria-label={`Bet4Peer on ${s.name}`}
                                        data-testid={s.testid}
                                        className="group inline-flex items-center justify-center w-10 h-10 rounded-full border border-white/10 bg-white/5 text-white/70 hover:text-[#003B71] hover:bg-[#FFD800] hover:border-[#FFD800] hover:scale-110 hover:shadow-[0_4px_14px_rgba(255,216,0,0.35)] transition-all duration-200"
                                    >
                                        <Icon className="w-[18px] h-[18px]" strokeWidth={2} />
                                    </a>
                                );
                            })}
                        </div>
                    </div>

                    {/* Links */}
                    <div>
                        <h4 className="font-semibold text-white mb-4">{t('footer.platform')}</h4>
                        <ul className="space-y-2">
                            <li>
                                <Link to="/bets" className="text-white/50 hover:text-[#00FF66] text-sm transition-colors">
                                    {t('footer.browseBets')}
                                </Link>
                            </li>
                            <li>
                                <Link to="/create" className="text-white/50 hover:text-[#00FF66] text-sm transition-colors">
                                    {t('footer.createBet')}
                                </Link>
                            </li>
                            <li>
                                <Link to="/sponsor" className="text-white/50 hover:text-[#00FF66] text-sm transition-colors">
                                    {t('footer.sponsorProgram')}
                                </Link>
                            </li>
                            <li>
                                <Link to="/dashboard" className="text-white/50 hover:text-[#00FF66] text-sm transition-colors">
                                    {t('footer.dashboard')}
                                </Link>
                            </li>
                        </ul>
                    </div>

                    {/* Resources */}
                    <div>
                        <h4 className="font-semibold text-white mb-4">{t('footer.resources')}</h4>
                        <ul className="space-y-2">
                            <li>
                                <Link to="/b4p-launch" className="text-white/50 hover:text-[#FFD700] text-sm transition-colors font-semibold">
                                    {t('footer.b4pLaunch')}
                                </Link>
                            </li>
                            <li>
                                <Link to="/presale" className="text-white/50 hover:text-[#FFD700] text-sm transition-colors">
                                    {t('footer.presale')}
                                </Link>
                            </li>
                            <li>
                                <Link to="/airdrop" className="text-white/50 hover:text-[#FF6B35] text-sm transition-colors">
                                    {t('footer.airdrop')}
                                </Link>
                            </li>
                            <li>
                                <Link to="/tokenomics" className="text-white/50 hover:text-[#00FF66] text-sm transition-colors">
                                    {t('footer.tokenomics')}
                                </Link>
                            </li>
                            <li>
                                <Link to="/roadmap" className="text-white/50 hover:text-[#00FF66] text-sm transition-colors">
                                    {t('footer.roadmap')}
                                </Link>
                            </li>
                            <li>
                                <Link to="/faq" className="text-white/50 hover:text-[#00FF66] text-sm transition-colors">
                                    {t('footer.faq')}
                                </Link>
                            </li>
                            <li>
                                <Link to="/b4p" className="text-white/50 hover:text-[#00FF66] text-sm transition-colors">
                                    {t('footer.b4pToken')}
                                </Link>
                            </li>
                            <li>
                                <Link to="/dex-guide" className="text-white/50 hover:text-[#0066CC] text-sm transition-colors">
                                    {t('footer.dexGuide')}
                                </Link>
                            </li>
                            <li>
                                <Link to="/liquidity" className="text-white/50 hover:text-[#00FF66] text-sm transition-colors">
                                    {t('footer.dexLiquidity')}
                                </Link>
                            </li>
                            <li>
                                <a href="#" className="text-white/50 hover:text-[#00FF66] text-sm transition-colors">
                                    {t('footer.smartContract')}
                                </a>
                            </li>
                            <li>
                                <a href="#" className="text-white/50 hover:text-[#00FF66] text-sm transition-colors">
                                    {t('footer.umaOracle')}
                                </a>
                            </li>
                        </ul>
                    </div>
                </div>

                {/* Bottom */}
                <div className="border-t border-white/5 mt-12 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
                    <p className="text-white/30 text-sm">
                        © 2026 Bet4Peer. {t('footer.rights')}
                    </p>
                    <div className="flex items-center gap-3 flex-wrap justify-center" data-testid="footer-actions">
                        {isConnected ? (
                            <button
                                onClick={disconnect}
                                data-testid="footer-disconnect-wallet"
                                className="group inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-bold border-2 border-white/20 bg-white/5 text-white hover:border-[#FFD800] hover:bg-[#FFD800] hover:text-[#003B71] transition-all duration-200 hover:scale-105 hover:shadow-[0_4px_14px_rgba(255,216,0,0.35)]"
                                aria-label={t('footer.disconnectWallet')}
                            >
                                <Wallet className="w-3.5 h-3.5" />
                                <span className="font-mono">{formatAddress(address)}</span>
                                <LogOut className="w-3 h-3 opacity-60 group-hover:opacity-100" />
                            </button>
                        ) : (
                            <button
                                onClick={connect}
                                disabled={isConnecting}
                                data-testid="footer-connect-wallet"
                                className="group inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-bold bg-[#FFD800] text-[#003B71] border-2 border-[#FFD800] hover:bg-[#FFC800] hover:scale-105 hover:shadow-[0_4px_14px_rgba(255,216,0,0.45)] disabled:opacity-60 disabled:cursor-not-allowed transition-all duration-200"
                                aria-label={t('footer.connectWalletAria')}
                            >
                                <Wallet className="w-3.5 h-3.5" />
                                {isConnecting ? t('common.connecting') : t('common.connectWallet')}
                            </button>
                        )}
                        <div className="flex items-center gap-2 text-xs text-white/40">
                            <span>{t('footer.poweredBy')}</span>
                            <span className="px-2 py-1 rounded bg-white/5 font-mono text-[#6F3FCA]">Base</span>
                            <span>+</span>
                            <span className="px-2 py-1 rounded bg-white/5 font-mono text-[#FFD700]">UMA</span>
                        </div>
                    </div>
                </div>
            </div>
        </footer>
    );
}
